import { useState, useMemo } from "react";
import { NumberField, Stat, Donut, ToolShell } from "./parts";
import { inr, inrShort } from "@/lib/format";

export function RoiCalculator() {
  const [purchase, setPurchase] = useState(5000000);
  const [extra, setExtra] = useState(500000);
  const [sale, setSale] = useState(8500000);
  const [annualRent, setAnnualRent] = useState(240000);
  const [years, setYears] = useState(7);

  const { invested, totalRent, netProfit, roi, annualized } = useMemo(() => {
    const invested = purchase + extra;
    const totalRent = annualRent * years;
    const netProfit = (sale - invested) + totalRent;
    const roi = invested ? (netProfit / invested) * 100 : 0;
    const annualized = invested && years ? (Math.pow((sale + totalRent) / invested, 1 / years) - 1) * 100 : 0;
    return { invested, totalRent, netProfit, roi, annualized };
  }, [purchase, extra, sale, annualRent, years]);

  return (
    <ToolShell title="ROI Calculator" desc="Measure the true return on a property investment — factoring purchase, add-on costs, rental income and final sale value.">
      <div className="grid gap-10 lg:grid-cols-2">
        <div className="space-y-8">
          <NumberField label="Purchase Price" value={purchase} onChange={setPurchase} min={500000} max={200000000} step={100000} prefix="₹" testid="roi-purchase" />
          <NumberField label="Additional Costs (reg, reno)" value={extra} onChange={setExtra} min={0} max={20000000} step={50000} prefix="₹" testid="roi-extra" />
          <NumberField label="Expected Sale Value" value={sale} onChange={setSale} min={500000} max={300000000} step={100000} prefix="₹" testid="roi-sale" />
          <NumberField label="Annual Rental Income" value={annualRent} onChange={setAnnualRent} min={0} max={10000000} step={10000} prefix="₹" testid="roi-rent" />
          <NumberField label="Holding Period" value={years} onChange={setYears} min={1} max={30} step={1} suffix="yrs" testid="roi-years" />
        </div>
        <div className="space-y-5">
          <Stat label="Total ROI" value={roi.toFixed(1) + "%"} highlight testid="roi-result" />
          <div className="grid grid-cols-2 gap-3">
            <Stat label="Annualised ROI" value={annualized.toFixed(1) + "%"} />
            <Stat label="Net Profit" value={inrShort(netProfit)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Stat label="Invested" value={inrShort(invested)} />
            <Stat label="Rent Earned" value={inrShort(totalRent)} />
          </div>
          <Donut data={[{ name: "Invested", value: invested }, { name: "Net Profit", value: Math.max(0, netProfit) }]} />
        </div>
      </div>
    </ToolShell>
  );
}
